import type {
  LocalizedField,
  MediaEntry,
  SequenceScript,
  SubitemScript,
  TrainingScript,
  TranslationUpdatePatch,
  TranslationsByLocale,
  TrainingTranslationsPatch,
  ChapterTranslationsPatch,
  SequenceTranslationsPatch,
  SubitemTranslationsPatch,
  MediaTranslationsPatch,
  ChapterScript
} from './types';
import { flattenSubitems } from './ScriptTranslationView.utils';

const mergeFieldFromTranslations = (
  field: LocalizedField | null | undefined,
  translations: TranslationsByLocale | undefined,
  locale: string
): void => {
  if (!field || !translations) return;
  const raw = translations[locale];
  if (raw == null) return;

  field.target = raw;
  field.missing = raw.trim().length === 0;
  if (field.stale !== undefined) field.stale = false;
};

const mergeTraining = (
  draft: TrainingScript,
  patch: TrainingTranslationsPatch | undefined,
  locale: string
): void => {
  if (!patch || !draft.training) return;

  mergeFieldFromTranslations(
    draft.training.title,
    patch.title_translations,
    locale
  );
  mergeFieldFromTranslations(
    draft.training.description,
    patch.description_translations,
    locale
  );
};

const mergeChapters = (
  chapters: ChapterScript[],
  patches: ChapterTranslationsPatch[] | undefined,
  locale: string
): void => {
  if (!patches) return;

  const chaptersById = new Map(chapters.map(chapter => [chapter.id, chapter]));

  for (const patch of patches) {
    const chapter = chaptersById.get(patch.id);
    if (!chapter) continue;

    mergeFieldFromTranslations(chapter.title, patch.title_translations, locale);
  }
};

const buildSequenceIndex = (
  chapters: ChapterScript[]
): Map<number, SequenceScript> => {
  const index = new Map<number, SequenceScript>();

  for (const chapter of chapters) {
    for (const sequence of chapter.sequences) {
      index.set(sequence.id, sequence);
    }
  }

  return index;
};

const mergeSequenceMedia = (
  sequence: SequenceScript,
  mediaPatches: MediaTranslationsPatch[] | undefined,
  locale: string
): void => {
  if (!mediaPatches || !sequence.media) return;

  const mediaById = new Map(
    sequence.media.map(mediaItem => [mediaItem.id, mediaItem])
  );

  for (const patch of mediaPatches) {
    const mediaItem = mediaById.get(patch.id);
    if (!mediaItem) continue;

    mergeFieldFromTranslations(
      mediaItem.script,
      patch.script_translations,
      locale
    );
    mergeFieldFromTranslations(mediaItem.name, patch.name_translations, locale);
  }
};

const mergeSubitemPatches = (
  sequence: SequenceScript,
  patches: SubitemTranslationsPatch[] | undefined,
  locale: string
): void => {
  if (!patches) return;

  const allSubitems = flattenSubitems(sequence.subitems);
  const subitemsById = new Map(
    allSubitems.map(subitem => [subitem.id, subitem])
  );

  for (const patch of patches) {
    const subitem = subitemsById.get(patch.id);
    if (!subitem) continue;

    mergeFieldFromTranslations(subitem.title, patch.title_translations, locale);
    mergeFieldFromTranslations(
      subitem.subtitle,
      patch.subtitle_translations,
      locale
    );
    mergeFieldFromTranslations(subitem.body, patch.body_translations, locale);

    if (!subitem.media || !patch.media) continue;

    const mediaById = new Map(
      subitem.media.map(mediaItem => [mediaItem.id, mediaItem])
    );

    for (const mediaPatch of patch.media) {
      const mediaItem = mediaById.get(mediaPatch.id);
      if (!mediaItem) continue;

      mergeFieldFromTranslations(
        mediaItem.script,
        mediaPatch.script_translations,
        locale
      );
      mergeFieldFromTranslations(
        mediaItem.name,
        mediaPatch.name_translations,
        locale
      );
    }
  }
};

const mergeSequences = (
  chapters: ChapterScript[],
  patches: SequenceTranslationsPatch[] | undefined,
  locale: string
): void => {
  if (!patches) return;

  const sequencesById = buildSequenceIndex(chapters);

  for (const patch of patches) {
    const sequence = sequencesById.get(patch.id);
    if (!sequence) continue;

    mergeFieldFromTranslations(
      sequence.title,
      patch.title_translations,
      locale
    );
    mergeSequenceMedia(sequence, patch.media, locale);
    mergeSubitemPatches(sequence, patch.subitems, locale);
  }
};

export function mergeUpdateTranslationsResponseIntoDraft(
  draft: TrainingScript,
  patch: TranslationUpdatePatch,
  locale: string
): TrainingScript {
  if (!draft) return draft;

  mergeTraining(draft, patch.training, locale);
  mergeChapters(draft.chapters, patch.chapters, locale);
  mergeSequences(draft.chapters, patch.sequences, locale);

  return draft;
}
